import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Table, Spinner, Row, Col, InputGroup, Badge } from "react-bootstrap";
import { FaDownload, FaCheck, FaExclamationTriangle, FaEye, FaEyeSlash, FaSearch, FaTimes, FaCamera, FaUsers } from "react-icons/fa";
import * as XLSX from 'xlsx';

const SendStudentLoginsModal = ({ show, onHide, schoolid, getQR, getClasses }) => {
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [showLinks, setShowLinks] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, className: '' });

  // Filter states
  const [classGradeFilter, setClassGradeFilter] = useState("all");
  const [classAcademicYearFilter, setClassAcademicYearFilter] = useState("all");
  const [classSnapshotFilter, setClassSnapshotFilter] = useState("all");

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  // Academic years - will be populated from actual data
  const [availableAcademicYears, setAvailableAcademicYears] = useState([
    { value: "all", label: "All Academic Years" }
  ]);

  // Snapshot filter options
  const snapshotOptions = [
    { value: "all", label: "All Classes" },
    { value: "active", label: "Active Classes Only" },
    { value: "snapshot", label: "Snapshots Only" }
  ];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      fetchClasses();
      resetForm();
    }
  }, [show, schoolid]);

  // Filter classes based on all criteria
  useEffect(() => {
    const filtered = classes.filter(classItem => {
      const matchesGrade = classGradeFilter === "all" || classItem.gradeBoolean === classGradeFilter;
      const matchesAcademicYear = classAcademicYearFilter === "all" || classItem.academicYear === classAcademicYearFilter;
      
      // Snapshot filtering logic
      let matchesSnapshot = true;
      if (classSnapshotFilter === "active") {
        matchesSnapshot = !classItem.snapshot;
      } else if (classSnapshotFilter === "snapshot") {
        matchesSnapshot = classItem.snapshot;
      }
      
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           (classItem.id && classItem.id.toString().includes(classSearchTerm));
      
      return matchesGrade && matchesAcademicYear && matchesSnapshot && matchesSearch;
    });
    
    setFilteredClasses(filtered);
  }, [classes, classSearchTerm, classGradeFilter, classAcademicYearFilter, classSnapshotFilter]);

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  const resetForm = () => {
    setSelectedClasses([]);
    setAllStudents([]);
    setSelectedStudents([]);
    setAlert({ show: false, type: "", message: "" });
    setShowLinks(false);
    setLoadingProgress({ current: 0, total: 0, className: '' });
    setClassSearchTerm("");
    setClassGradeFilter("all");
    setClassAcademicYearFilter("all");
    setClassSnapshotFilter("all");
  };

  const generateBypassLink = (student) => {
    const assessmentURL = process.env.REACT_APP_QR_CODE_BASE_URL + "/?code=";
    let removeHyphens = student.id.replaceAll("-", "");
    let first32 = removeHyphens.substring(0, 32);
    let newQrCode = first32.concat(student.password);
    return assessmentURL.concat(newQrCode);
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "", message: "" }), 5000);
  };

  const fetchClasses = async () => {
    if (!schoolid) return;
    
    setLoading(true);
    try {
      // Use the correct API call that matches SwapStudentsAssessmentModal
      const response = await getClasses(schoolid);
      console.log("Classes response:", response);
      
      // Transform classes data to include enhanced properties
      const transformedClasses = (response || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);
        
        return {
          id: classItem.id,
          name: classItem.name || 'Unnamed Class',
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: classItem.academicYear || null, // Use direct field from API
          snapshot: classItem.snapshot || false, // Add snapshot field
          studentNumber: classItem.studentNumber || 0, // Add student count
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated
        };
      }).sort((a, b) => {
        // Sort by academic year (newest first), then by name
        if (a.academicYear !== b.academicYear) {
          return b.academicYear?.localeCompare(a.academicYear || '') || 0;
        }
        return a.name.localeCompare(b.name);
      });

      // Extract unique academic years for filter options
      const uniqueAcademicYears = [...new Set(transformedClasses
        .map(cls => cls.academicYear)
        .filter(year => year) // Remove null/undefined
      )].sort().reverse(); // Sort newest first

      const academicYearOptions = [
        { value: "all", label: "All Academic Years" },
        ...uniqueAcademicYears.map(year => ({
          value: year,
          label: `${year} Academic Year`
        }))
      ];

      setAvailableAcademicYears(academicYearOptions);
      setClasses(transformedClasses);
    } catch (error) {
      showAlert("error", "Failed to fetch classes. Please try again.");
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsForClasses = async (classIds) => {
    if (!classIds || classIds.length === 0) return;
    
    setLoading(true);
    setLoadingProgress({ current: 0, total: classIds.length, className: '' });
    
    try {
      const allStudentsData = [];
      
      // Loop through each selected class
      for (let i = 0; i < classIds.length; i++) {
        const classId = classIds[i];
        const currentClass = classes.find(cls => cls.id == classId);
        const className = currentClass?.name || `Class ${classId}`;
        
        setLoadingProgress({ current: i + 1, total: classIds.length, className });
        
        try {
          // Get QR data for this class
          const qrData = await getQR(classId);
          
          // Handle different possible response structures
          let studentsData = [];
          if (qrData.students) {
            studentsData = qrData.students;
          } else if (qrData.data && qrData.data.students) {
            studentsData = qrData.data.students;
          } else if (Array.isArray(qrData)) {
            studentsData = qrData;
          } else if (qrData.data && Array.isArray(qrData.data)) {
            studentsData = qrData.data;
          }
          
          // Format students data for this class
          const formattedStudents = studentsData.map(student => ({
            id: `${classId}-${student.id}`, // Make unique across classes
            originalId: student.id,
            name: student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.username,
            username: student.username || student.login || student.id,
            password: student.password || '********',
            parentEmail: student.parentEmail || student.parent_email || student.email,
            studentEmail: student.studentEmail || student.student_email,
            class: className,
            classId: classId,
            isSnapshot: currentClass?.snapshot || false, // Add snapshot info
            bypassLink: '' // Will be generated when needed
          }));

          // Generate bypass links for each student
          const studentsWithLinks = formattedStudents.map(student => ({
            ...student,
            bypassLink: generateBypassLink({ id: student.originalId, password: student.password })
          }));

          allStudentsData.push(...studentsWithLinks);
        } catch (error) {
          console.error(`Error fetching students for class ${classId}:`, error);
          // Continue with other classes even if one fails
        }
      }

      setAllStudents(allStudentsData);
      setSelectedStudents(allStudentsData.map(s => s.id)); // Select all by default
    } catch (error) {
      showAlert("error", "Failed to fetch student data. Please try again.");
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0, className: '' });
    }
  };

  const handleClassToggle = (classId) => {
    const newSelectedClasses = selectedClasses.includes(classId)
      ? selectedClasses.filter(id => id !== classId)
      : [...selectedClasses, classId];
    
    setSelectedClasses(newSelectedClasses);
    
    // Fetch students for selected classes
    if (newSelectedClasses.length > 0) {
      fetchStudentsForClasses(newSelectedClasses);
    } else {
      setAllStudents([]);
      setSelectedStudents([]);
    }
  };

  const handleSelectAllClasses = () => {
    // Use filtered classes for "select all" functionality
    const visibleClassIds = filteredClasses.map(cls => cls.id);
    const allVisibleSelected = visibleClassIds.every(id => selectedClasses.includes(id));
    
    if (allVisibleSelected) {
      // Deselect all visible classes
      const newSelectedClasses = selectedClasses.filter(id => !visibleClassIds.includes(id));
      setSelectedClasses(newSelectedClasses);
      if (newSelectedClasses.length > 0) {
        fetchStudentsForClasses(newSelectedClasses);
      } else {
        setAllStudents([]);
        setSelectedStudents([]);
      }
    } else {
      // Select all visible classes (merge with already selected)
      const newSelectedClasses = [...new Set([...selectedClasses, ...visibleClassIds])];
      setSelectedClasses(newSelectedClasses);
      fetchStudentsForClasses(newSelectedClasses);
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === allStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(allStudents.map(s => s.id));
    }
  };

  const clearClassSearch = () => {
    setClassSearchTerm("");
  };

  const exportToExcel = async () => {
    if (selectedStudents.length === 0) {
      showAlert("warning", "Please select at least one student.");
      return;
    }

    setExporting(true);
    
    try {
      // Get selected students data
      const selectedStudentsData = allStudents.filter(student => 
        selectedStudents.includes(student.id)
      );

      // Group students by class
      const studentsByClass = {};
      selectedStudentsData.forEach(student => {
        if (!studentsByClass[student.class]) {
          studentsByClass[student.class] = [];
        }
        studentsByClass[student.class].push(student);
      });

      const classNames = Object.keys(studentsByClass);
      
      // For now, create a single Excel file with all data
      const excelData = selectedStudentsData.map(student => ({
        'Class': student.class,
        'Student Name': student.name,
        'Bypass Link': student.bypassLink,
        'Class Type': student.isSnapshot ? 'Snapshot' : 'Active'
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [
        { wch: 20 }, // Class
        { wch: 25 }, // Student Name
        { wch: 80 }, // Bypass Link
        { wch: 12 }  // Class Type
      ];
      worksheet['!cols'] = columnWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Student Bypass Links');

      const currentDate = new Date().toISOString().split('T')[0];
      const filename = classNames.length === 1 
        ? `Student_Bypass_Links_${classNames[0].replace(/[^a-zA-Z0-9]/g, '_')}_${currentDate}.xlsx`
        : `Student_Bypass_Links_Multiple_Classes_${currentDate}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
      
      const snapshotCount = selectedStudentsData.filter(s => s.isSnapshot).length;
      const activeCount = selectedStudentsData.length - snapshotCount;
      
      showAlert("success", `Successfully exported ${selectedStudentsData.length} student bypass links from ${classNames.length} class(es). ${activeCount} from active classes, ${snapshotCount} from snapshots.`);
    } catch (error) {
      showAlert("error", "Failed to export to Excel. Please try again.");
      console.error("Error exporting to Excel:", error);
    } finally {
      setExporting(false);
    }
  };

  // Calculate if all visible classes are selected
  const visibleClassIds = filteredClasses.map(cls => cls.id);
  const allVisibleSelected = visibleClassIds.length > 0 && visibleClassIds.every(id => selectedClasses.includes(id));
  
  // Check for snapshot classes in selection
  const hasSnapshotClasses = selectedClasses.some(classId => 
    classes.find(cls => cls.id === classId)?.snapshot
  );

  const snapshotStudentCount = allStudents.filter(s => s.isSnapshot).length;

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaDownload className="me-2" />
          Export Student Bypass Links
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '600px' }}>
        {alert.show && (
          <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        <Form>
          {/* Class Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Select Classes</Form.Label>
            {loading && classes.length === 0 ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading classes...</span>
              </div>
            ) : (
              <>
                {/* Filter Controls */}
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Label className="small">Academic Year</Form.Label>
                    <Form.Select 
                      value={classAcademicYearFilter} 
                      onChange={(e) => setClassAcademicYearFilter(e.target.value)}
                      size="sm"
                    >
                      {availableAcademicYears.map(year => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">Grade Level</Form.Label>
                    <Form.Select 
                      value={classGradeFilter} 
                      onChange={(e) => setClassGradeFilter(e.target.value)}
                      size="sm"
                    >
                      {gradeLevels.map(grade => (
                        <option key={grade.value} value={grade.value}>
                          {grade.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">Class Type</Form.Label>
                    <Form.Select 
                      value={classSnapshotFilter} 
                      onChange={(e) => setClassSnapshotFilter(e.target.value)}
                      size="sm"
                    >
                      {snapshotOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small">Search</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search classes..."
                        value={classSearchTerm}
                        onChange={(e) => setClassSearchTerm(e.target.value)}
                      />
                      {classSearchTerm && (
                        <Button
                          variant="outline-secondary"
                          onClick={clearClassSearch}
                          style={{ borderLeft: 'none' }}
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </InputGroup>
                  </Col>
                </Row>

                {/* Filter Summary */}
                <div className="mb-3">
                  <small className="text-muted">
                    Showing {filteredClasses.length} of {classes.length} classes
                    {(classGradeFilter !== "all" || classAcademicYearFilter !== "all" || classSnapshotFilter !== "all" || classSearchTerm) && 
                      " (filtered)"}
                  </small>
                </div>

                {/* Select All Button */}
                <div className="mb-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleSelectAllClasses}
                    disabled={filteredClasses.length === 0}
                  >
                    {allVisibleSelected ? "Deselect All Visible" : "Select All Visible"} 
                    {(classGradeFilter !== "all" || classAcademicYearFilter !== "all" || classSnapshotFilter !== "all" || classSearchTerm) && " (Filtered)"}
                  </Button>
                  {selectedClasses.length > 0 && (
                    <span className="ms-2 text-muted">
                      ({selectedClasses.length} total selected)
                    </span>
                  )}
                </div>

                {/* Classes List */}
                <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "0.375rem", padding: "0.5rem" }}>
                  {filteredClasses.length === 0 ? (
                    <div className="text-center py-2 text-muted">
                      {classSearchTerm || classGradeFilter !== "all" || classAcademicYearFilter !== "all" || classSnapshotFilter !== "all" 
                        ? "No classes match your filters" 
                        : "No classes available"}
                    </div>
                  ) : (
                    filteredClasses.map(cls => (
                      <Form.Check
                        key={cls.id}
                        type="checkbox"
                        id={`class-${cls.id}`}
                        label={
                          <div className="d-flex align-items-center justify-content-between w-100">
                            <div>
                              <span className="fw-medium">{cls.name}</span>
                              <div>
                                <Badge bg="secondary" className="me-1">{cls.grade}</Badge>
                                {cls.academicYear && (
                                  <Badge bg="info" className="me-1">{cls.academicYear}</Badge>
                                )}
                                {cls.snapshot && (
                                  <Badge bg="secondary" className="me-1">
                                    <FaCamera className="me-1" size={10} />
                                    Snapshot
                                  </Badge>
                                )}
                                {cls.trial_class && (
                                  <Badge bg="warning" text="dark" className="me-1">Trial</Badge>
                                )}
                                {!cls.snapshot && cls.studentNumber > 0 && (
                                  <Badge bg="light" text="dark">
                                    <FaUsers className="me-1" size={10} />
                                    {cls.studentNumber} students
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        }
                        checked={selectedClasses.includes(cls.id)}
                        onChange={() => handleClassToggle(cls.id)}
                        className="mb-2"
                      />
                    ))
                  )}
                </div>
                <small className="text-muted">
                  Selected: {selectedClasses.length} of {classes.length} classes
                  {(classGradeFilter !== "all" || classAcademicYearFilter !== "all" || classSnapshotFilter !== "all" || classSearchTerm) && 
                    ` (${filteredClasses.length} visible)`}
                </small>
              </>
            )}
          </Form.Group>

          {/* Snapshot Warning */}
          {hasSnapshotClasses && (
            <Alert variant="warning" className="mb-3">
              <FaExclamationTriangle className="me-2" />
              <strong>Snapshot Classes Selected:</strong> You have selected snapshot classes. 
              These contain historical student data and bypass links.
            </Alert>
          )}

          {/* Loading Progress */}
          {loading && loadingProgress.total > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small>Loading students...</small>
                <small>{loadingProgress.current} of {loadingProgress.total}</small>
              </div>
              <div className="progress mb-2">
                <div 
                  className="progress-bar" 
                  style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
                ></div>
              </div>
              <small className="text-muted">Currently loading: {loadingProgress.className}</small>
            </div>
          )}

          {selectedClasses.length > 0 && (
            <>
              {/* Student List */}
              {!loading && allStudents.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6>
                      Students ({allStudents.length})
                      {snapshotStudentCount > 0 && (
                        <Badge bg="secondary" className="ms-2">
                          <FaCamera className="me-1" size={10} />
                          {snapshotStudentCount} from snapshots
                        </Badge>
                      )}
                    </h6>
                    <div>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setShowLinks(!showLinks)}
                        className="me-2"
                      >
                        {showLinks ? <FaEyeSlash /> : <FaEye />}
                        {showLinks ? " Hide" : " Show"} Links
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {selectedStudents.length === allStudents.length ? "Deselect All" : "Select All"}
                      </Button>
                    </div>
                  </div>

                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Class</th>
                          <th>Student Name</th>
                          {showLinks && <th>Bypass Link</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {allStudents.map(student => (
                          <tr key={student.id} className={student.isSnapshot ? 'table-warning' : ''}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleStudentToggle(student.id)}
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {student.class}
                                {student.isSnapshot && (
                                  <Badge bg="secondary" className="ms-2">
                                    <FaCamera className="me-1" size={10} />
                                    Snapshot
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td>{student.name}</td>
                            {showLinks && (
                              <td>
                                <small style={{ wordBreak: "break-all" }}>
                                  {student.bypassLink}
                                </small>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="mt-3">
                    <small className="text-muted">
                      Selected: {selectedStudents.length} of {allStudents.length} students
                      {snapshotStudentCount > 0 && 
                        ` (${snapshotStudentCount} from snapshot classes)`
                      }
                    </small>
                  </div>
                </>
              ) : !loading && (
                <div className="text-center py-4 text-muted">
                  No students found for selected classes.
                </div>
              )}
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedStudents.length > 0 && (
              <span>
                Exporting {selectedStudents.length} student links from {selectedClasses.length} class{selectedClasses.length !== 1 ? 'es' : ''}
                {snapshotStudentCount > 0 && (
                  <Badge bg="warning" className="ms-2 text-dark">
                    <FaCamera className="me-1" size={10} />
                    {snapshotStudentCount} from snapshots
                  </Badge>
                )}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} disabled={exporting} className="me-2">
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={exportToExcel}
              disabled={selectedClasses.length === 0 || selectedStudents.length === 0 || exporting}
            >
              {exporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <FaDownload className="me-2" />
                  Export Excel ({selectedStudents.length} students)
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SendStudentLoginsModal;